import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";
import { useNavigate } from "react-router-dom";

const Profile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    address: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  // Fetch profile and address on mount
  useEffect(() => {
    async function fetchProfile() {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }
        const res = await axios.get(`${API_BASE_URL}/customers/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfile(res.data);
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err.response?.data?.error || "Failed to load profile",
        });
        if (err.response?.status === 401) navigate("/login");
      }
    }
    fetchProfile();
  }, [navigate]);

  // Handle profile form submission
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_BASE_URL}/customers/profile`,
        {
          name: profile.name,
          phone: profile.phone,
          gender: profile.gender,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Profile updated successfully",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.error || "Failed to update profile",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle address form submission
  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/customers/address`,
        profile.address || {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Address saved successfully",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.error || "Failed to save address",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle address deletion
  const handleDeleteAddress = async () => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: "This will delete your address.",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${API_BASE_URL}/customers/address`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfile((prev) => ({ ...prev, address: null }));
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Address deleted successfully",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err.response?.data?.error || "Failed to delete address",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Container className="py-5">
      <h2 className="mb-4 fw-bold text-center">Manage Profile</h2>
      <Row className="justify-content-center g-4">
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0">
            <Card.Body>
              <Card.Title className="mb-4">Profile Information</Card.Title>
              <Form onSubmit={handleProfileSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    required
                    disabled={isLoading}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={profile.email}
                    disabled
                    readOnly
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    disabled={isLoading}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Gender</Form.Label>
                  <Form.Select
                    value={profile.gender}
                    onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
                    disabled={isLoading}
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
                <Button type="submit" variant="primary" disabled={isLoading}>
                  {isLoading ? "Saving..." : "Save Profile"}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0">
            <Card.Body>
              <Card.Title className="mb-4">Address</Card.Title>
              <Form onSubmit={handleAddressSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Address Line 1</Form.Label>
                  <Form.Control
                    type="text"
                    value={profile.address?.address_line1 || ""}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        address: { ...profile.address, address_line1: e.target.value },
                      })
                    }
                    required
                    disabled={isLoading}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Address Line 2</Form.Label>
                  <Form.Control
                    type="text"
                    value={profile.address?.address_line2 || ""}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        address: { ...profile.address, address_line2: e.target.value },
                      })
                    }
                    disabled={isLoading}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    value={profile.address?.city || ""}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        address: { ...profile.address, city: e.target.value },
                      })
                    }
                    required
                    disabled={isLoading}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    value={profile.address?.state || ""}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        address: { ...profile.address, state: e.target.value },
                      })
                    }
                    required
                    disabled={isLoading}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={profile.address?.postal_code || ""}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        address: { ...profile.address, postal_code: e.target.value },
                      })
                    }
                    required
                    disabled={isLoading}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    value={profile.address?.country || ""}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        address: { ...profile.address, country: e.target.value },
                      })
                    }
                    required
                    disabled={isLoading}
                  />
                </Form.Group>
                <Button type="submit" variant="primary" disabled={isLoading} className="me-2">
                  {isLoading ? "Saving..." : "Save Address"}
                </Button>
                {profile.address && (
                  <Button
                    variant="danger"
                    onClick={handleDeleteAddress}
                    disabled={isLoading}
                  >
                    Delete Address
                  </Button>
                )}
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;